import {
  EvidenceTypeEnum,
  getApiLogisticsV1LoadsEvidenceListQueryKey,
  getApiTradeV1SiteMaterialsEvidenceListQueryKey,
  useApiLogisticsV1LoadsEvidenceList,
  useApiQualitySessionsV1SiteMaterialSessionsEvidenceList,
  useApiQualitySessionsV1TradeCompanySessionEvidenceList,
  useApiTradeV1SiteMaterialsEvidenceList,
  useQueryClient,
} from 'api';
import { useMemo } from 'react';

import { getPhotoVariant, useBaleAnalysis } from '../../../grade';

export const useBaleEvidence = ({
  siteMaterialId,
  loadId,
  tradeCompanySessionId,
  siteMaterialSessionId,
}: {
  siteMaterialId?: number;
  loadId?: number;
  tradeCompanySessionId?: number;
  siteMaterialSessionId?: number;
}) => {
  const queryClient = useQueryClient();

  const { data: siteMaterialEvidence, isLoading: isSiteMaterialLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    String(siteMaterialId),
    {
      page_size: 10000,
      type: EvidenceTypeEnum.BP,
    },
    {
      query: {
        enabled: Boolean(siteMaterialId),
      },
    }
  );

  const { data: loadEvidence, isLoading: isLoadLoading } = useApiLogisticsV1LoadsEvidenceList(
    String(loadId),
    {
      page_size: 10000,
      type: EvidenceTypeEnum.BP,
    },
    {
      query: {
        enabled: Boolean(loadId),
      },
    }
  );

  const { data: tradeCompanySessionEvidence, isLoading: isTradeCompanySessionLoading } =
    useApiQualitySessionsV1TradeCompanySessionEvidenceList(
      Number(tradeCompanySessionId),
      {
        page_size: 10000,
        type: EvidenceTypeEnum.BP,
      },
      {
        query: {
          enabled: Boolean(tradeCompanySessionId),
        },
      }
    );

  const { data: siteMaterialSessionEvidence, isLoading: isSiteMaterialSessionLoading } =
    useApiQualitySessionsV1SiteMaterialSessionsEvidenceList(
      Number(siteMaterialSessionId),
      {
        page_size: 10000,
        type: EvidenceTypeEnum.BP,
      },
      {
        query: {
          enabled: Boolean(siteMaterialSessionId),
        },
      }
    );

  const data = siteMaterialId
    ? siteMaterialEvidence
    : loadEvidence || tradeCompanySessionEvidence || siteMaterialSessionEvidence;

  const { bales, addBalePhoto, isBaleLoading, removeBalePhoto } = useBaleAnalysis();

  const analyzingBales = useMemo(
    () => bales.filter((bale) => bale.isLoading || (bale.uploadProgress !== undefined && bale.uploadProgress < 100)),
    [bales]
  );

  const uploadProgress = useMemo(
    () =>
      analyzingBales.length > 0
        ? analyzingBales.reduce((acc, bale) => acc + (bale.uploadProgress ?? 0), 0) / analyzingBales.length
        : 0,
    [analyzingBales]
  );

  const carouselMedia = useMemo(
    () => data?.results.map((result) => String(result.preview || result.file)) ?? [],
    [data?.results]
  );

  const completedBales = useMemo(() => bales.filter((bale) => !bale.isLoading), [bales]);

  if (completedBales.length > 0) {
    queryClient.invalidateQueries({
      queryKey: getApiTradeV1SiteMaterialsEvidenceListQueryKey(String(siteMaterialId)),
    });
    queryClient.invalidateQueries({
      queryKey: getApiLogisticsV1LoadsEvidenceListQueryKey(String(loadId)),
    });
  }

  const allPhotos = useMemo(
    () =>
      data?.results.map(({ file, preview, model_results }) => ({
        disabled: !model_results,
        isLoading: false,
        src: (preview || file) ?? '',
        variant: getPhotoVariant(model_results ?? undefined),
      })),
    [data?.results]
  );

  return {
    addBalePhoto,
    allPhotos,
    analyzingBales,
    bales,
    carouselMedia,
    completedBales,
    data,
    isBaleLoading:
      isBaleLoading ||
      isSiteMaterialLoading ||
      isLoadLoading ||
      isTradeCompanySessionLoading ||
      isSiteMaterialSessionLoading,
    removeBalePhoto,
    uploadProgress,
  };
};
